import { getCookie } from '../utils/cookieUtils';
import logger from '../utils/Logger';
import { convertWebmToWav } from '../utils/audioConversionUtils';


// S3 Audio Upload Functions
export async function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        resolve(result);
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
};

export async function putFileToS3 (presignedUrl, fileBlob, mimeType = "audio/wav") {
  try {
    // If not already WAV, convert
    let uploadBlob = fileBlob;
    let uploadMime = mimeType;
    if (fileBlob.type !== 'audio/wav') {
      uploadBlob = await convertWebmToWav(fileBlob);
      uploadMime = 'audio/wav';
    }
    const response = await fetch(presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": uploadMime },
      body: uploadBlob,
    });
    return response.ok;
  } catch (error) {
    console.error(":x: Upload failed:", error);
    return false;
  }
};
export async function setupS3AudioRecording(
    stream,
    deps
    ) {
    try {
      // Check if stream is active before creating MediaRecorder
      if (!stream.active) {
        console.error('[setupS3AudioRecording] ❌ Stream is INACTIVE - cannot create MediaRecorder');
        deps.setS3UploadStatus('Error: Microphone stream is inactive');
        return null;
      }
      // Determine the best MIME type for recording
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        console.warn('webm/opus not supported, trying alternatives...');
        if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else {
          mimeType = 'audio/wav';
        }
      }


      const recorder = new MediaRecorder(stream, {
        mimeType: mimeType
      });

       // Clear previous chunks
       deps.audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          
          deps.audioChunksRef.current.push(event.data);
           // Update global reference
        if (window.mediaRecorderRef) {
          window.mediaRecorderRef.audioChunks = deps.audioChunksRef.current;
        }
      }
      };

      recorder.onstop = async () => {
        if (deps.audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(deps.audioChunksRef.current, { type: mimeType });
          // Store blob size for verification
          const blobSizeMB = (audioBlob.size / (1024 * 1024)).toFixed(2);
        }
      };
      recorder.onerror = (event) => {
      logger.error('S3 MediaRecorder error occurred', {
        component: 'useAudioSetup',
        action: 'S3_RECORDER_ERROR',
        userId: deps.getAuthenticatedUserId(),
        context: {
          conversationId: deps.conversationId || getCookie('conversation_id'),
          sessionId: deps.sessionId || getCookie('session_id'),
          mimeType,
          audioChunksCount: deps.audioChunksRef.current?.length || 0
        },
        error: { message: event.error?.message || String(event.error) }
      });
      console.error('❌ S3 MediaRecorder error:', event.error);
      deps.setS3UploadStatus(`S3 Recording error: ${event.error}`);
    };

    deps.mediaRecorderRef.current = recorder;
    deps.setS3AudioRecorder(recorder);

     // Update global reference
    window.mediaRecorderRef = {
      current: recorder,
      audioChunks: deps.audioChunksRef.current
    };
    return recorder;
    } catch (error) {
      logger.error('Failed to setup S3 audio recording', {
        component:  'useAudioSetup',
        action: 'S3_RECORDER_SETUP_FAILED',
        userId: deps.getAuthenticatedUserId(),
        context: {
          conversationId: deps.conversationId || getCookie('conversation_id'),
          streamActive: stream?.active || false
        },
        error
      });
      console.error('Error setting up S3 audio recording:', error);
      deps.setS3UploadStatus(`Error setting up S3 audio recording: ${error.message}`);
      return null;
    }
  };

  export function stopAssemblyStream(deps) {
    // Clear any pending reconnection attempts
    if (deps.reconnectionTimeoutRef.current) {
      clearTimeout(deps.reconnectionTimeoutRef.current);
      deps.reconnectionTimeoutRef.current = null;
    }

    // Stop the transcription watchdog - recording is ending, nothing to guard
    if (deps.stopTranscriptionWatchdog) {
      deps.stopTranscriptionWatchdog(deps);
    }

    // Reset reconnection attempts
    deps.reconnectionAttemptsRef.current = 0;
    
    if (deps.assemblySocketRef.current) {
      deps.assemblySocketRef.current.close();
    }
    if (deps.workletNodeRef.current) {
      deps.workletNodeRef.current.disconnect();
    }
    if (deps.audioContextRef.current) {
      deps.audioContextRef.current.close();
    }
    if (deps.streamRef.current) {
      deps.streamRef.current.getTracks().forEach(t => t.stop());
    }
    if (deps.mediaRecorderRef.current) {
      const state = deps.mediaRecorderRef.current.state;
      if (state === 'recording' || state === 'paused') {
        deps.mediaRecorderRef.current.stop();
      }
    }
     // Stop S3 audio recording
    if (deps.s3AudioRecorder && deps.s3AudioRecorder.state === 'recording') {
      deps.userInitiatedStopRef.current = false;
      deps.s3AudioRecorder.stop();
    }


    deps.setStatus("Stopped");
    cancelAnimationFrame(deps.animationFrameRef.current);
  };

export async function startTranscriptionStream(deps) {
     // Don't start if user initiated stop or session is stopping
    if (deps.userInitiatedStopRef.current || window.isStoppingSession) {
      return;
    }
    // Check if already connected
  if (deps.assemblySocketRef.current && deps.assemblySocketRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    const storedToken = deps.getStoredToken();
    
    if (storedToken) {
      deps.startTranscriptionStreamWithToken(storedToken);
      return;
    }
    
    // If no stored token, wait for configuration response
    deps.setStatus("Waiting for token...");
  };

  export function createTranscriptionSocket(
  token,transcriptionModel,deepgramWsBaseUrl,assemblyWsBaseUrl,configDetails
) {

  // Deepgram socket creation
  if (
    transcriptionModel ===configDetails.TRANSCRIPTION_MODEL_DEEPGRAM
  ) {

    return {
      serviceName: 'Deepgram',

      socketUrl: deepgramWsBaseUrl,

      socket: new window.WebSocket(
        deepgramWsBaseUrl,
        ['bearer', token]
      )
    };
  }

  // AssemblyAI validation
  if (!assemblyWsBaseUrl) {
    throw new Error('assemblyWsBaseUrl is not configured');
  }

  // AssemblyAI URL
  const assemblyWsUrl =
    `${assemblyWsBaseUrl}?sample_rate=16000&token=${token}`;

  // AssemblyAI socket creation
  return {
    serviceName: 'AssemblyAI',

    socketUrl: assemblyWsUrl,

    socket: new window.WebSocket(assemblyWsUrl)
  };
}

export async function initializeAudioStream(deps) {
   if (deps.audioInitializationRef.current) {
        logger.warn("Audio initialization already running");
        return;
    }

  deps.audioInitializationRef.current = true;
  try{
  deps.streamRef.current =
    await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 16000
      }
    });

  // Setup S3 audio recording
  await deps.setupS3AudioRecording(
    deps.streamRef.current,
    {
      mediaRecorderRef: deps.mediaRecorderRef,
      audioChunksRef: deps.audioChunksRef,
      setS3UploadStatus: deps.setS3UploadStatus,
      setS3AudioRecorder: deps.setS3AudioRecorder,
      conversationId: deps.conversationId,
      sessionId: deps.sessionId,
      getAuthenticatedUserId: deps.getAuthenticatedUserId
    }
  );

  // Start S3 recording
  const userId = deps.getAuthenticatedUserId();

  await deps.startAudioS3Recording(deps.conversationId || deps.getCookie('conversation_id'),userId,deps.streamRef.current);

  // Audio context
  const audioContext =
  new window.AudioContext({
    sampleRate: 16000
  });
  // Reset chunks
  deps.audioChunksRef.current = [];

  // Analyser
  const analyser = audioContext.createAnalyser();
  deps.analyserRef.current = analyser;

  analyser.fftSize = 128;

  // Source
  const source =
  audioContext.createMediaStreamSource(
    deps.streamRef.current
  );

  source.connect(analyser);

  // AudioWorklet processor
  const processorCode = `
    class PCMProcessor extends AudioWorkletProcessor {
      process(inputs) {
        const input = inputs[0];

        if (input.length > 0) {

          const f32 = input[0];

          const int16 = new Int16Array(f32.length);

          for (let i = 0; i < f32.length; i++) {
            let s = Math.max(-1, Math.min(1, f32[i]));
            int16[i] =
              s < 0
                ? s * 0x8000
                : s * 0x7fff;
          }

          this.port.postMessage(int16.buffer);
        }

        return true;
      }
    }

    registerProcessor("pcm-processor", PCMProcessor);
  `;

  const blob =
    new Blob(
      [processorCode],
      { type: 'application/javascript' }
    );

  const moduleURL = URL.createObjectURL(blob);

  await audioContext.audioWorklet.addModule(moduleURL);

  const workletNode = new window.AudioWorkletNode(audioContext, "pcm-processor");

  deps.audioContextRef.current = audioContext;
  deps.workletNodeRef.current = workletNode;

  return {source,workletNode};
  }finally{
    deps.audioInitializationRef.current = false;
  }
}

export function setupWorkletMessageHandler(
  workletNode,
  deps
) {

  workletNode.port.onmessage = (e) => {

    const now = Date.now();

    deps.audioWorkletActiveRef.current = true;

    // Telemetry: track when the mic pipeline last produced audio
    if (deps.lastMicActivityRef) {
      deps.lastMicActivityRef.current = now;
    }

    // PCM processor already sends Int16 data
    const int16Data = new Int16Array(e.data);

    const hasAudio =Array.from(int16Data).some(s => Math.abs(s) > 200);

    deps.speakingRef.current = hasAudio;

    // Send audio to transcription socket
    if (!deps.isPausedRef.current) {

      const currentSocket =deps.assemblySocketRef.current;

      const socketReady =currentSocket &&currentSocket.readyState === WebSocket.OPEN;

      const isReconnecting =deps.isReconnectingRef.current;

      if (socketReady && !isReconnecting) {

        try {

          currentSocket.send(int16Data.buffer);

          // Telemetry: remember when audio was last successfully sent to Deepgram
          deps.lastAudioSentRef.current = now;

        } catch (error) {

          console.warn(
            '⚠️ [AudioWorklet] Failed to send audio to transcription socket, queuing...',
            error
          );

          deps.audioQueueRef.current.push(int16Data.buffer);
        }

      } else {

        deps.audioQueueRef.current.push(int16Data.buffer);

        // Prevent memory explosion
        if (deps.audioQueueRef.current.length > (deps.configDetails?.AUDIO_QUEUE_MAX_BUFFERS || 100)) {
          const previousLength = deps.audioQueueRef.current.length;
          deps.audioQueueRef.current =deps.audioQueueRef.current.slice(-(deps.configDetails?.AUDIO_QUEUE_KEEP_BUFFERS || 50));
          const droppedCount = previousLength - deps.audioQueueRef.current.length;
          // ~640 bytes per buffer (128 samples x 2 bytes at 16kHz) = ~20ms of audio each
          const droppedSeconds = ((droppedCount * 0.02)).toFixed(2);
          deps.lastAudioDroppedRef.current = { count: droppedCount, seconds: parseFloat(droppedSeconds), at: now };
          deps.droppedAudioTotalMsRef.current += droppedCount * 20;
          logger.warn(
            'Audio queue overflow - dropping oldest queued audio',
            {
              component: 'useAudioSetup',
              action: 'AUDIO_QUEUE_OVERFLOW_DROP',
              userId: deps.getAuthenticatedUserId?.(),
              context: {
                conversationId: deps.conversationId || deps.getCookie?.('conversation_id'),
                sessionId: deps.sessionId,
                queueLengthBefore: previousLength,
                droppedBuffers: droppedCount,
                droppedAudioSeconds: parseFloat(droppedSeconds),
                droppedAudioTotalMs: deps.droppedAudioTotalMsRef.current,
                socketState: currentSocket ? currentSocket.readyState : null,
                isReconnecting: isReconnecting,
                isPaused: deps.isPausedRef.current
              }
            }
          );
        }
      }
    }

    // Send audio chunks to backend websocket
    if (deps.socketRef.current?.readyState === WebSocket.OPEN &&!deps.isPausedRef.current &&(now - deps.getLastTransmissionTime()) >=
        deps.transmissionInterval) {

      if (!hasAudio) {
        return;
      }

      // Optional audio chunk streaming
      if (deps.sendAudioChunk === 'true') {

        const audioBytes =new Uint8Array(e.data);

        const base64Audio =btoa(String.fromCharCode(...audioBytes));

        const audioPayload = {
          action: "audio_chunk",
          session_id: deps.sessionId,
          audio_data: base64Audio,
          format: "pcm_s16le",
          sample_rate: 16000,
          channels: 1,
          timestamp: now
        };

        deps.socketRef.current.send(JSON.stringify(audioPayload));
      }

      deps.setLastTransmissionTime(now);
    }
  };
}

export function setupTranscriptionSocketHandlers(
  assemblySocket,
  deps
) {

  // =========================
  // ON OPEN
  // =========================

  assemblySocket.onopen = async () => {

    const currentModel =deps.getStoredModel();

    const serviceName =currentModel ===deps.configDetails.TRANSCRIPTION_MODEL_DEEPGRAM? 'Deepgram': 'AssemblyAI';
    logger.info(
      'Transcription WebSocket connection established',
      {
        component: 'useAudioSetup',
        action: 'TRANSCRIPTION_CONNECTED',
        userId: deps.getAuthenticatedUserId(),
        context: {
          service: serviceName,
          transcriptionModel: currentModel,
          wsUrl: deps.socketUrl ? deps.socketUrl.split('?')[0] : null,

          conversationId: deps.conversationId || deps.getCookie('conversation_id'),
          sessionId: deps.sessionId,
          readyState: assemblySocket.readyState,
          browserOnline: navigator.onLine,
          isPaused: deps.isPausedRef.current,
          isReconnecting: deps.isReconnectingRef.current
        }
      }
    );

    // Prevent stale socket race
    if (deps.assemblySocketRef.current !== assemblySocket) {
      return;
    }

    // Reset reconnect state
    deps.isReconnectingRef.current = false;

    deps.setIsReconnecting(false);

    deps.reconnectionAttemptsRef.current = 0;

    if (deps.reconnectionTimeoutRef.current) {

      clearTimeout(
        deps.reconnectionTimeoutRef.current
      );

      deps.reconnectionTimeoutRef.current =null;
    }

    // =========================
    // KEEP ALIVE
    // =========================

    const isPausedFromCookie = deps.getCookie('is_recording_paused') === 'true';

    const currentModelForKeepAlive = deps.getStoredModel();

    if (isPausedFromCookie && currentModelForKeepAlive === deps.configDetails.TRANSCRIPTION_MODEL_DEEPGRAM) {

      if (deps.keepAliveIntervalRef.current) {

        clearInterval(deps.keepAliveIntervalRef.current);

        deps.keepAliveIntervalRef.current =null;
      }

      deps.keepAliveIntervalRef.current =
        setInterval(() => {

          if (
            deps.assemblySocketRef.current && deps.assemblySocketRef.current.readyState === WebSocket.OPEN
          ) {

            try {

              deps.assemblySocketRef.current.send(JSON.stringify({type: "KeepAlive"}));

            } catch (error) {

              console.warn(
                '⚠️ [KeepAlive] Failed to send KeepAlive:',
                error
              );
            }
          }

        }, 3000);
    }

    // =========================
    // RESTORE TIMER
    // =========================

    if (deps.reconnectStartedAtRef.current) {

      const reconnectionSeconds =Math.floor((Date.now() - deps.reconnectStartedAtRef.current) / 1000);

      if (reconnectionSeconds > 0) {

        const prevPause =Number(deps.getCookie('total_pause_duration') || 0);

        deps.setCookie('total_pause_duration',String(prevPause + reconnectionSeconds));
      }

      deps.reconnectStartedAtRef.current =null;
    }

    const correctTimerValue =deps.calculateElapsedTime();

    deps.setTimer(correctTimerValue);

    deps.lastKnownTimerRef.current =correctTimerValue;

    if (deps.lastTranscriptTimeRef) {
      deps.lastTranscriptTimeRef.current = Date.now();
    }

    if (deps.startTranscriptionWatchdog) {
      deps.startTranscriptionWatchdog(deps);
    }

    // =========================
    // AUDIO INITIALIZATION
    // =========================

    try {

      const {
        source,
        workletNode
      } = await deps.initializeAudioStream({

        streamRef: deps.streamRef,
        audioContextRef: deps.audioContextRef,
        analyserRef: deps.analyserRef,
        workletNodeRef: deps.workletNodeRef,
        audioInitializationRef: deps.audioInitializationRef,
        audioChunksRef: deps.audioChunksRef,
        mediaRecorderRef: deps.mediaRecorderRef,

        setS3UploadStatus: deps.setS3UploadStatus,

        setS3AudioRecorder: deps.setS3AudioRecorder,

        conversationId: deps.conversationId,

        sessionId: deps.sessionId,

        getAuthenticatedUserId: deps.getAuthenticatedUserId,

        setupS3AudioRecording: deps.setupS3AudioRecording,

        startAudioS3Recording:deps.startAudioS3Recording,

        getCookie: deps.getCookie
      });

      let lastTransmissionTime = 0;

      const transmissionInterval = 50;

      deps.setupWorkletMessageHandler(
        workletNode,
        {
          audioWorkletActiveRef: deps.audioWorkletActiveRef,
          speakingRef: deps.speakingRef,
          isPausedRef: deps.isPausedRef,
          assemblySocketRef: deps.assemblySocketRef,
          isReconnectingRef: deps.isReconnectingRef,
          audioQueueRef: deps.audioQueueRef,
          lastAudioSentRef: deps.lastAudioSentRef,
          lastMicActivityRef: deps.lastMicActivityRef,
          lastAudioDroppedRef: deps.lastAudioDroppedRef,
          droppedAudioTotalMsRef: deps.droppedAudioTotalMsRef,
          configDetails: deps.configDetails,
          getAuthenticatedUserId: deps.getAuthenticatedUserId,
          getCookie: deps.getCookie,
          conversationId: deps.conversationId,
          sessionId: deps.sessionId,
          socketRef: deps.socketRef,
          sendAudioChunk: deps.sendAudioChunk,
          transmissionInterval,
          getLastTransmissionTime: () => lastTransmissionTime,
          setLastTransmissionTime: (value) => { lastTransmissionTime = value;}
        }
      );

      source.connect(workletNode);

      deps.setStatus("🎙️ Listening...");

      deps.setShowMicLoadingModal(false);

      const existingStart =parseInt( deps.getCookie('recording_start_time'), 10 );

      if (!existingStart || isNaN(existingStart)) {
        deps.saveRecordingStartTime(Date.now());
      }

      deps.saveRecordingStatus("🎙️ Listening...");

      setTimeout(() => {
        
      deps.flushAudioQueue({
        audioQueueRef:
          deps.audioQueueRef,
        assemblySocketRef:
          deps.assemblySocketRef,
        isReconnectingRef:
          deps.isReconnectingRef,
        lastKnownTimerRef:
          deps.lastKnownTimerRef,
        setIsReconnecting:
          deps.setIsReconnecting,
        setTimer:
          deps.setTimer
      });
      }, 100);

    } catch (error) {

      logger.error(
        'Failed to initialize microphone',
        {
          component: 'useAudioSetup',
          action: 'MICROPHONE_INIT_FAILED',

          userId:deps.getAuthenticatedUserId(),

          context: {
            conversationId:
              deps.conversationId ||
              deps.getCookie(
                'conversation_id'
              ),

            sessionId:deps.sessionId,

            transcriptionModel:deps.getStoredModel() || null
          },

          error
        }
      );

      deps.setStatus("Error setting up audio");

      deps.setShowMicLoadingModal(false);
    }
  };

  // =========================
  // ON ERROR
  // =========================

  assemblySocket.onerror = (error) => {

    const currentModel = deps.getStoredModel();

    const serviceName = currentModel === deps.configDetails.TRANSCRIPTION_MODEL_DEEPGRAM ? 'Deepgram' : 'AssemblyAI';

    logger.error(
      'Transcription WebSocket error',
      {
        component: 'useAudioSetup',
        action: 'TRANSCRIPTION_ERROR',
        userId: deps.getAuthenticatedUserId(),

        context: {service: serviceName,
          conversationId:
            deps.conversationId ||
            deps.getCookie(
              'conversation_id'
            ),

          sessionId: deps.sessionId
        },

        error: {
          message:
            error.message ||
            'Unknown transcription service error'
        }
      }
    );

    deps.setStatus(`${serviceName} connection error`);
  };

  // =========================
  // ON CLOSE
  // =========================

  assemblySocket.onclose = (event) => {

    const currentModel = deps.getStoredModel();

    const serviceName = currentModel === deps.configDetails.TRANSCRIPTION_MODEL_DEEPGRAM ? 'Deepgram' : 'AssemblyAI';

    logger.warn(
      'Transcription WebSocket connection closed',
      {
        component: 'useAudioSetup',
        action: 'TRANSCRIPTION_CLOSED',

        userId:
          deps.getAuthenticatedUserId(),

        context: {
          service: serviceName,

          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,

          conversationId:
            deps.conversationId ||
            deps.getCookie(
              'conversation_id'
            ),

          sessionId: deps.sessionId,

          userInitiatedStop: deps.userInitiatedStopRef.current,

          isStoppingSession: window.isStoppingSession
        }
      }
    );

    // User stopped session
    if (deps.userInitiatedStopRef.current || window.isStoppingSession) {
      return;
    }

    const currentStatus = deps.statusRef.current;

    // Session already stopped
    if (currentStatus === "Stopping..." || currentStatus === "Stopped" || currentStatus === "Processing..." || currentStatus === "Ready") {
      return;
    }

    const now = Date.now();
    const lastAudioSent = deps.lastAudioSentRef?.current || 0;
    const lastWorkletActivity = deps.lastMicActivityRef?.current || 0;
    const micActiveRecently =
      (lastAudioSent > 0 && now - lastAudioSent < deps.configDetails.TRANSCRIPTION_WATCHDOG_MIC_ACTIVE_THRESHOLD_MS) ||
      (lastWorkletActivity > 0 && now - lastWorkletActivity < deps.configDetails.TRANSCRIPTION_WATCHDOG_MIC_ACTIVE_THRESHOLD_MS);

    // Reconnect
    // Code 1000 (normal closure) is user-initiated: never reconnect.
    // Code 1011 (Deepgram idle timeout) IS reconnectable when the mic is still active
    // and the meeting is live - see micActiveRecently above.
    const canReconnect = !deps.isReconnectingRef.current && deps.reconnectionAttemptsRef.current < deps.configDetails.MAX_RECONNECTION_ATTEMPTS;

    if (event.code === 1011 && micActiveRecently && canReconnect) {

      logger.warn(
        'Deepgram closed with 1011 while audio was still flowing - auto-reconnecting',
        {
          component: 'useAudioSetup',
          action: 'TRANSCRIPTION_1011_AUTO_RECONNECT',
          userId: deps.getAuthenticatedUserId(),
          context: {
            conversationId: deps.conversationId || deps.getCookie('conversation_id'),
            sessionId: deps.sessionId,
            code: event.code,
            reason: event.reason,
            msSinceLastAudioSent: lastAudioSent > 0 ? now - lastAudioSent : null,
            msSinceLastWorkletActivity: lastWorkletActivity > 0 ? now - lastWorkletActivity : null,
            reconnectionAttempts: deps.reconnectionAttemptsRef.current
          }
        }
      );

      deps.handleAssemblyDisconnection();
      return;
    }

    if (event.code !== 1000 && event.code !== 1011 && !deps.isReconnectingRef.current && deps.reconnectionAttemptsRef.current < deps.configDetails.MAX_RECONNECTION_ATTEMPTS) {

      deps.handleAssemblyDisconnection();
    } else if (event.code === 1011) {
      // Deepgram idle timeout with no recent audio - genuinely silent mic, notify user
      logger.info('Transcription timed out due to inactivity (Deepgram code 1011)', {
        component: 'useAudioSetup',
        action: 'TRANSCRIPTION_IDLE_TIMEOUT',
        userId: deps.getAuthenticatedUserId(),
        context: {
          conversationId: deps.conversationId || deps.getCookie('conversation_id'),
          sessionId: deps.sessionId,
          code: event.code,
          reason: event.reason,
          micActiveRecently,
          msSinceLastAudioSent: lastAudioSent > 0 ? now - lastAudioSent : null,
          msSinceLastWorkletActivity: lastWorkletActivity > 0 ? now - lastWorkletActivity : null
        }
      });
      deps.setStatus('Transcription timed out - no audio detected');
    }
  };
}

export function setupTranscriptMessageHandler(
  assemblySocket,
  deps
) {

  assemblySocket.onmessage = (message) => {

    const data = JSON.parse(message.data);

    const currentModel = deps.getStoredModel();

    const res = deps.normalizeTranscriptMessage(data, currentModel);

    deps.handleTranscriptMessage(res);
  };
}

/* =========================
   TRANSCRIPTION WATCHDOG
   Detects a broken transcription path while the meeting is still live:
   the mic keeps producing audio but no transcript has been received for a
   long time. Forces an automatic reconnect so the transcript gap stays small.
   ========================= */

export function startTranscriptionWatchdog(deps) {
  // Avoid duplicate watchdogs
  if (deps.transcriptionWatchdogRef?.current) {
    return;
  }

  const intervalMs = deps.configDetails?.TRANSCRIPTION_WATCHDOG_INTERVAL_MS || 15000;

  deps.transcriptionWatchdogRef.current = setInterval(() => {

    // Session stopping or paused - nothing to guard
    if (deps.userInitiatedStopRef.current || window.isStoppingSession) {
      return;
    }

    if (deps.isPausedRef.current) {
      return;
    }

    // Only guard while actively listening
    const currentStatus = deps.statusRef.current;
    if (currentStatus !== "\uD83C\uDFA7 Listening...") {
      return;
    }

    const now = Date.now();

    const socket = deps.assemblySocketRef.current;
    const socketState = socket ? socket.readyState : null;

    const lastMicActivity = deps.lastMicActivityRef?.current || 0;
    const lastAudioSent = deps.lastAudioSentRef?.current || 0;
    const lastTranscript = deps.lastTranscriptTimeRef?.current || 0;

    const msSinceLastMicActivity = now - lastMicActivity;
    const msSinceLastAudioSent = lastAudioSent > 0 ? now - lastAudioSent : null;
    const msSinceLastTranscript = lastTranscript > 0 ? now - lastTranscript : null;
    const queueLength = deps.audioQueueRef.current ? deps.audioQueueRef.current.length : 0;

    // Mic pipeline dead - transcript silence is expected, do not fire
    if (!lastMicActivity || msSinceLastMicActivity > deps.configDetails.TRANSCRIPTION_WATCHDOG_MIC_ACTIVE_THRESHOLD_MS) {
      return;
    }

    // Speech audio was sent to the transcription service but no transcript came back
    // for too long - path is broken, force a reconnect.
    const unackedSpeech =
      msSinceLastAudioSent !== null &&
      msSinceLastAudioSent < deps.configDetails.TRANSCRIPTION_WATCHDOG_MIC_ACTIVE_THRESHOLD_MS &&
      (msSinceLastTranscript === null || msSinceLastTranscript > deps.configDetails.TRANSCRIPTION_WATCHDOG_UNACKED_SPEECH_THRESHOLD_MS);

    if (unackedSpeech) {

      logger.error(
        'Transcription watchdog: audio sent but no transcript received - forcing reconnect',
        {
          component: 'useAudioSetup',
          action: 'TRANSCRIPTION_WATCHDOG_TRIGGERED',
          userId: deps.getAuthenticatedUserId?.(),
          context: {
            conversationId: deps.conversationId || deps.getCookie?.('conversation_id'),
            sessionId: deps.sessionId,
            socketState,
            msSinceLastMicActivity,
            msSinceLastAudioSent,
            msSinceLastTranscript,
            queueLength,
            droppedAudioTotalMs: deps.droppedAudioTotalMsRef?.current || 0,
            reconnectionAttempts: deps.reconnectionAttemptsRef.current
          }
        }
      );

      // Reset the transcript timestamp so the watchdog does not re-fire every tick
      // while the reconnect is in progress.
      if (deps.lastTranscriptTimeRef) {
        deps.lastTranscriptTimeRef.current = Date.now();
      }

      if (typeof deps.forceAssemblyReconnect === 'function') {
        deps.forceAssemblyReconnect(deps);
      }
    }
  }, intervalMs);
}

export function stopTranscriptionWatchdog(deps) {
  if (deps?.transcriptionWatchdogRef?.current) {
    clearInterval(deps.transcriptionWatchdogRef.current);
    deps.transcriptionWatchdogRef.current = null;
  }
}

export function flushAudioQueue(deps) {

  const queueLength = deps.audioQueueRef.current.length;

  // No socket
  if (!deps.assemblySocketRef.current) {

    console.warn(
      '⚠️ [Audio Queue] No socket reference'
    );

    return;
  }

  const socketState =deps.assemblySocketRef.current.readyState;

  // Flush queued audio
  if (queueLength > 0 && socketState === WebSocket.OPEN) {

    let successCount = 0;

    deps.audioQueueRef.current.forEach(
      (audioBuffer, index) => {

        try {

          deps.assemblySocketRef.current.send(
            audioBuffer
          );

          successCount++;

        } catch (error) {

          console.error(
            `❌ [Audio Queue] Error sending buffer ${index + 1}:`,
            error
          );
        }
      }
    );

    // Clear queue
    deps.audioQueueRef.current = [];

    // Telemetry: log flush outcome (how much queued audio was recovered)
    const flushedSeconds = (successCount * 0.02).toFixed(2);

    logger.info(
      'Audio queue flushed to transcription socket',
      {
        component: 'useAudioSetup',
        action: 'AUDIO_QUEUE_FLUSHED',
        userId: deps.getAuthenticatedUserId?.(),
        context: {
          conversationId: deps.conversationId || deps.getCookie?.('conversation_id'),
          sessionId: deps.sessionId,
          queuedBuffers: queueLength,
          flushedBuffers: successCount,
          failedBuffers: queueLength - successCount,
          flushedAudioSeconds: parseFloat(flushedSeconds),
          droppedAudioTotalMs: deps.droppedAudioTotalMsRef?.current || 0
        }
      }
    );

    // Reset reconnect state
    deps.isReconnectingRef.current = false;

    deps.setIsReconnecting(false);

    // Restore timer
    if (
      deps.lastKnownTimerRef.current > 0
    ) {

      deps.setTimer(
        deps.lastKnownTimerRef.current
      );
    }

  }

  // Queue already empty
  else if (queueLength === 0) {

    deps.isReconnectingRef.current = false;

    deps.setIsReconnecting(false);

    // Restore timer
    if (
      deps.lastKnownTimerRef.current > 0
    ) {

      deps.setTimer(deps.lastKnownTimerRef.current);
    }

  }

  // Socket not ready
  else {

    console.warn(
      `⚠️ [Audio Queue] Cannot flush - Socket not ready (state: ${socketState}, expected: ${WebSocket.OPEN})`
    );

    console.warn(
      `⚠️ [Audio Queue] ${queueLength} buffers remain queued`
    );
  }
}

export function handleAssemblyDisconnection(
  deps
) {

  const userId =deps.getAuthenticatedUserId();

  // User stopped recording
  if (
    deps.userInitiatedStopRef.current
  ) {

    logger.debug(
      'Transcription disconnection ignored - user initiated stop',
      {
        component: 'useAudioSetup',
        action: 'DISCONNECTION_IGNORED',

        userId,

        context: {
          reason: 'user_initiated_stop',

          conversationId:
            deps.conversationId ||
            getCookie('conversation_id'),

          sessionId:
            deps.sessionId
        }
      }
    );

    return;
  }

  // Session stopping globally
  if (window.isStoppingSession) {

    logger.debug(
      'Transcription disconnection ignored - session stopping',
      {
        component: 'useAudioSetup',
        action: 'DISCONNECTION_IGNORED',

        userId,

        context: {
          reason: 'session_stopping',

          conversationId:
            deps.conversationId ||
            getCookie('conversation_id'),

          sessionId:
            deps.sessionId
        }
      }
    );

    return;
  }

  // Invalid lifecycle state
  const currentStatus = deps.statusRef.current;

  if (
    currentStatus === "Stopping..." ||
    currentStatus === "Processing..." ||
    currentStatus === "Ready"
  ) {

    logger.debug(
      'Transcription disconnection ignored - invalid status',
      {
        component: 'useAudioSetup',
        action: 'DISCONNECTION_IGNORED',

        userId,

        context: {
          reason: 'invalid_status',

          currentStatus,

          conversationId:
            deps.conversationId ||
            getCookie('conversation_id'),

          sessionId:
            deps.sessionId
        }
      }
    );

    return;
  }

  // Already reconnecting
  if (
    deps.isReconnectingRef.current
  ) {

    logger.debug(
      'Transcription disconnection ignored - already reconnecting',
      {
        component: 'useAudioSetup',
        action: 'DISCONNECTION_IGNORED',

        userId,

        context: {
          reason: 'already_reconnecting',

          conversationId:
            deps.conversationId ||
            getCookie('conversation_id'),

          sessionId:
            deps.sessionId
        }
      }
    );

    return;
  }

  const now = Date.now();

  const timeSinceLastReconnection = now - deps.lastReconnectionTimeRef.current;

  // Rate limiting
  if (timeSinceLastReconnection < deps.configDetails.RECONNECTION_DELAY_MS) {

    logger.debug(
      'Transcription disconnection ignored - rate limited',
      {
        component: 'useAudioSetup',
        action: 'DISCONNECTION_IGNORED',

        userId,

        context: {
          reason: 'rate_limited',

          timeSinceLastReconnection,

          reconnectionDelayMs:
            deps.configDetails
              .RECONNECTION_DELAY_MS,

          conversationId:
            deps.conversationId ||
            getCookie('conversation_id'),

          sessionId:
            deps.sessionId
        }
      }
    );

    return;
  }

  logger.warn(
    'Transcription WebSocket disconnected - initiating reconnection',
    {
      component: 'useAudioSetup',
      action: 'TRANSCRIPTION_DISCONNECTED',

      userId,

      context: {
        conversationId:
          deps.conversationId ||
          getCookie('conversation_id'),

        sessionId:
          deps.sessionId,

        timer:
          deps.timer,

        reconnectionAttempts:
          deps.reconnectionAttemptsRef.current,

        transcriptionModel:
          deps.getStoredModel() || null,

        timeSinceLastReconnection
      }
    }
  );

  // Reconnect state
  deps.isReconnectingRef.current =true;

  deps.setIsReconnecting(true);

  // Preserve timer
  deps.lastKnownTimerRef.current = deps.timer;

  deps.reconnectStartedAtRef.current = Date.now();

  // Max attempts reached
  if (deps.reconnectionAttemptsRef.current >= deps.configDetails.MAX_RECONNECTION_ATTEMPTS) {

    logger.error(
      'WebSocket reconnection max attempts reached',
      {
        component: 'useAudioSetup',
        action: 'RECONNECTION_MAX_ATTEMPTS',

        userId:
          deps.getAuthenticatedUserId(),

        context: {
          attempts:
            deps.reconnectionAttemptsRef.current,

          maxAttempts:
            deps.configDetails
              .MAX_RECONNECTION_ATTEMPTS,

          conversationId:
            deps.conversationId ||
            getCookie('conversation_id'),

          sessionId:
            deps.sessionId
        }
      }
    );

    console.error(
      '❌ [Reconnection] Max attempts reached, giving up'
    );

    deps.setStatus(
      "Connection failed - please restart recording"
    );

    deps.isReconnectingRef.current =false;

    deps.setIsReconnecting(false);

    return;
  }

  // Increment attempt
  deps.reconnectionAttemptsRef.current += 1;

  deps.lastReconnectionTimeRef.current =now;

  logger.info(
    'WebSocket reconnection attempt initiated',
    {
      component: 'useAudioSetup',
      action: 'RECONNECTION_ATTEMPT',

      userId:
        deps.getAuthenticatedUserId(),

      context: {
        attempt:
          deps.reconnectionAttemptsRef.current,

        maxAttempts:
          deps.configDetails
            .MAX_RECONNECTION_ATTEMPTS,

        conversationId:
          deps.conversationId ||
          getCookie('conversation_id'),

        sessionId:
          deps.sessionId
      }
    }
  );

  // Clear previous timeout
  if (deps.reconnectionTimeoutRef.current) {

    clearTimeout(deps.reconnectionTimeoutRef.current);
  }

  // Exponential backoff
  const delay = Math.min(deps.configDetails.RECONNECTION_DELAY_MS * deps.reconnectionAttemptsRef.current,
    10000
  );

  deps.reconnectionTimeoutRef.current =
    setTimeout(() => {

      // Double-check stop
      if (deps.userInitiatedStopRef.current || window.isStoppingSession) {

        deps.isReconnectingRef.current = false;

        deps.setIsReconnecting(false);

        return;
      }

      // Existing token
      const storedToken = deps.getStoredToken();

      if (storedToken) {
        deps.startTranscriptionStreamWithToken(storedToken);
      }

      // Request new token
      else {
        deps.requestTokenRenewal();
      }

    }, delay);
}

export async function forceAssemblyReconnect(
  deps
) {

  const userId = deps.getAuthenticatedUserId();

  // Prevent concurrent reconnects
  if (deps.reconnectionLockRef.current) {

    logger.debug(
      'Force reconnection blocked - lock active',
      {
        component: 'useAudioSetup',
        action: 'FORCE_RECONNECT_BLOCKED',

        userId,

        context: {
          reason:
            'reconnection_lock_active',

          conversationId:
            deps.conversationId ||
            getCookie('conversation_id'),

          sessionId:
            deps.sessionId
        }
      }
    );

    return;
  }

  logger.info(
    'Force transcription reconnection initiated',
    {
      component: 'useAudioSetup',
      action: 'FORCE_RECONNECT_START',

      userId,

      context: {
        conversationId:
          deps.conversationId ||
          getCookie('conversation_id'),

        sessionId:
          deps.sessionId,

        timer:
          deps.timer,

        transcriptionModel:
          deps.getStoredModel() || null,

        assemblySocketState:
          deps.assemblySocketRef.current
            ?.readyState || null,

        hasAudioContext:
          !!deps.audioContextRef.current,

        hasStream:
          !!deps.streamRef.current
      }
    }
  );

  // Lock reconnect
  deps.reconnectionLockRef.current =true;

  // Clear timeout
  if (deps.reconnectionTimeoutRef.current) {
    clearTimeout(deps.reconnectionTimeoutRef.current);
    deps.reconnectionTimeoutRef.current =null;
  }

  // Reset reconnect state
  deps.reconnectionAttemptsRef.current =0;

  deps.isReconnectingRef.current =true;

  deps.setIsReconnecting(true);

  // Preserve timer
  deps.lastKnownTimerRef.current =deps.timer;

  deps.reconnectStartedAtRef.current =Date.now();

  // =========================
  // SOCKET CLEANUP
  // =========================

  if (deps.assemblySocketRef.current) {

    try {

      const oldSocket =deps.assemblySocketRef.current;

      // Remove handlers
      oldSocket.onopen = null;
      oldSocket.onmessage = null;
      oldSocket.onerror = null;
      oldSocket.onclose = null;

      // Close socket
      if (oldSocket.readyState === WebSocket.OPEN || oldSocket.readyState === WebSocket.CONNECTING) {

        oldSocket.close();
      }

    } catch (e) {

      console.warn(
        '[forceAssemblyReconnect] Error closing socket:',
        e
      );
    }

    deps.assemblySocketRef.current = null;
  }

  // =========================
  // WORKLET CLEANUP
  // =========================

  if (deps.workletNodeRef.current) {

    try {

      deps.workletNodeRef.current.disconnect();

      deps.workletNodeRef.current = null;

    } catch (e) {

      console.warn(
        '[forceAssemblyReconnect] Error disconnecting worklet:',
        e
      );
    }
  }

  // =========================
  // AUDIO CONTEXT CLEANUP
  // =========================

  if (deps.audioContextRef.current) {

    try {

      if (deps.audioContextRef.current.state !== 'closed') {

        await deps.audioContextRef.current.close();
      }

    } catch (e) {

      console.warn(
        '[forceAssemblyReconnect] Error closing audio context:',
        e
      );
    }

    deps.audioContextRef.current =null;
  }

  // =========================
  // S3 RECORDER CLEANUP
  // =========================

  if (deps.s3AudioRecorder) {

    try {

      if (deps.s3AudioRecorder.state === 'recording' || deps.s3AudioRecorder.state === 'paused') {

        deps.s3AudioRecorder.stop();
      }

      // Stop recorder stream
      if (deps.s3AudioRecorder.stream) {

        deps.s3AudioRecorder.stream
          .getTracks()
          .forEach(track => {
            track.stop();
          });
      }

    } catch (e) {

      console.warn(
        '[forceAssemblyReconnect] Error stopping old S3 recorder:',
        e
      );
    }

    deps.setS3AudioRecorder(null);

    deps.setS3AudioChunks([]);
  }

  // =========================
  // STREAM CLEANUP
  // =========================

  if (deps.streamRef.current) {

    try {

      deps.streamRef.current
        .getTracks()
        .forEach(track => {
          track.stop();
        });

    } catch (e) {

      console.warn(
        '[forceAssemblyReconnect] Error stopping old stream:',
        e
      );
    }

    deps.streamRef.current = null;
  }

  // =========================
  // RESTART STREAM
  // =========================

  setTimeout(() => {

    const storedToken =deps.getStoredToken();

    if (storedToken) {

      deps.startTranscriptionStreamWithToken(storedToken);

    } else {

      deps.requestTokenRenewal();
    }

    // Unlock reconnect
    setTimeout(() => {

      deps.reconnectionLockRef.current = false;

    }, 3000);

  }, 300);
}